import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import type { SpacingValues } from "@brexhq/shared/types/SpacingScale";

export enum FormValueRetrievalType {
  QUERY = "query",
  COOKIE = "cookie",
}

export enum DataType {
  EMAIL = "email",
  TEXT = "text",
  URL = "url",
  SELECT = "select",
  CURRENCY = "currency",
  TEXTAREA = "textArea",
  TELEPHONE = "telephone",
  RADIOBUTTONS = "radioButtons",
  CHECKBOXES = "checkboxes",
  HIDDEN = "hidden",
}

export enum FormStatus {
  FORM_LOADING = "formLoading",
  FORM_LOAD_SUCCESS = "formLoadedSuccess",
  FORM_LOAD_FAILED = "formLoadedFailed",
  SUBMIT_ATTEMPTED = "submitAttempted",
  SUBMITTING = "submitting",
  SUCCESS = "success",
  FAIL = "fail",
}

export enum MarketoVisibilityRuleType {
  ALWAYS_SHOW = "alwaysShow",
  SHOW = "show",
  HIDE = "hide",
}

export enum MarketoVisibilityOperators {
  IS = "is",
  IS_NOT = "isNot",
  IS_EMPTY = "isEmpty",
  IS_NOT_EMPTY = "isNotEmpty",
  STARTS_WITH = "startsWith",
  NOT_STARTS_WITH = "notStartsWith",
  CONTAINS = "contains",
  NOT_CONTAINS = "notContains",
}

export type SubmitError =
  | {
      title: string;
      message: string;
    }
  | null
  | undefined;

export type FormValues = {
  [key: string]: string | number | boolean | Array<string>;
};

type BaseField<TDataType extends DataType, TValue> = {
  dataType: TDataType;
  value: TValue;
  name: string;
  label: string;
  required: boolean;
  error?: React.ReactNode;
  placeholder?: string;
};

type SelectValues = {
  options: Array<{
    isDefault: boolean;
    label: string;
    selected: boolean;
    value: string;
  }>;
};

type RadioButtonGroupValues = {
  options: Array<{
    label: string;
    value: string;
  }>;
};

type CheckboxGroupValues = {
  options: Array<CheckboxOptionValues>;
};

export type CheckboxOptionValues = {
  label: string;
  value: string;
  selected: boolean;
};

export type UrlField = BaseField<DataType.URL, string>;
export type EmailField = BaseField<DataType.EMAIL, string>;
export type TextField = BaseField<DataType.TEXT, string> & {
  maxLength?: number;
};
export type TextAreaField = BaseField<DataType.TEXTAREA, string> & {
  maxLength?: number;
};
export type CurrencyField = BaseField<DataType.CURRENCY, string>;
export type TelelphoneField = BaseField<DataType.TELEPHONE, string>;
export type HiddenField = BaseField<DataType.HIDDEN, string>;

export type SelectField = BaseField<DataType.SELECT, string> & SelectValues;
export type RadioButtonField = BaseField<DataType.RADIOBUTTONS, string> &
  RadioButtonGroupValues;
export type CheckboxField = BaseField<DataType.CHECKBOXES, Array<string>> &
  CheckboxGroupValues;

export type Field =
  | EmailField
  | TextField
  | UrlField
  | TextAreaField
  | SelectField
  | CurrencyField
  | TelelphoneField
  | RadioButtonField
  | CheckboxField
  | HiddenField;

export type FieldState = {
  [key: string]: Field;
};
export type SetFields = (setFields: (fields: FieldState) => FieldState) => void;

export type MarketoFormProps = {
  submitButtonLabel?: string;
  showBackground?: boolean;
  backgroundColor?: string;
  programName: string;
  source?: string;
  reason?: string;
  formId: number;
  bypassSuccessContentUrl?: {
    to: string | null;
    toExternal: string | null;
    noFollow?: boolean | null;
  };
  checkWorkEmail?: boolean | null;
  hideNonEmailFields?: boolean | null;
  isInlineEmail?: boolean | null;
  useArrowButtonOnMobile?: boolean | null;
  showSuccessMessageBackgroundColor?: boolean | null;
  hideSuccessMessageImage?: boolean | null;
  successMessage?: string | { title: string; description: string };
  FormHeaderComponent?: React.ReactNode;
  FormFooterComponent?: React.ReactNode;
  successCallback?: (formValues?: FormValues) => void;
  formLoadedCallback?: () => void;
  anchor?: string;
  topSpacing: SpacingValues;
  horizontalSpacing?: SpacingValues;
  isChilipiperEnabled?: boolean;
  isQualifiedEnabled?: boolean | null;
  qualifiedExperienceId?: string | null;
  theme?: ModuleThemeType;
  hiddenFields?: {
    contentType?: string;
    contentTag?: string;
    webinarId?: string;
    videoId?: string;
    eventId?: string;
    pageSegment?: string;
    brexSolution?: string;
    brexFeature?: string;
    salesforceCampaign?: string;
  };
};

export type RenderFieldProps = {
  field: Field;
  setFields: SetFields;
  submitAttempted: boolean;
  formId: number;
  isInlineEmail?: boolean;
};

export type MarketoFormField = {
  id: string;
  label: string;
  dataType: DataType;
  validationMessage: string;
  rowNumber: number;
  columnNumber?: number;
  maxLength?: number;
  required: boolean;
  formPrefill?: boolean;
  autoFill?: {
    value: string;
    valueFrom: string;
    parameterName: string;
  };
  visibilityRules?: {
    ruleType: MarketoVisibilityRuleType;
    rules?: Array<{
      subjectField: string;
      operator: MarketoVisibilityOperators;
      values: Array<string>;
      altLabel: string;
    }>;
  };
  hintText?: string;
  fieldMetaData?: {
    multiSelect?: number;
    values?: Array<{
      isDefault: boolean;
      label: string;
      selected: boolean;
      value: string;
    }>;
  };
};

export const genericErrorMessage = {
  title: "It’s not you it’s us",
  message:
    "Please refresh your page to try again. If the problem persists, contact support@brex.com",
};
